import React, { useState, memo } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircleIcon, ArrowRightIcon, StarIcon, MessageCircleIcon, LinkIcon, RefreshCwIcon, ShieldCheckIcon, ThumbsUpIcon, ChevronDownIcon } from 'lucide-react';
import { ScrollReveal } from '../components/ScrollReveal';
import { SEOHead } from '../components/SEOHead';
import { AnimatePresence } from 'framer-motion';
const faqs = [{
  question: 'How does automated review requesting work?',
  answer: 'When a job is marked as complete in your CRM or invoicing software, our system automatically sends a polite, customized text message and email to the customer thanking them for their business and asking for a review with a direct link.'
}, {
  question: 'Can I respond to reviews from one dashboard?',
  answer: 'Yes. You can view and reply to reviews from Google, Facebook, and major review platforms all from a single unified dashboard or directly from our mobile app. No more logging into multiple accounts.'
}, {
  question: 'What platforms do you manage reviews on?',
  answer: 'We focus on the platforms that matter most for local service businesses: Google Business Profile, Facebook, and the major review sites for your industry. We can also integrate with industry-specific platforms depending on your needs.'
}, {
  question: 'Do I need to be on every review platform?',
  answer: 'No. We analyze your specific market and industry to determine where your customers are actually looking. Usually, dominating Google plus one or two key review platforms is all a local service business needs to win their market.'
}];
const FAQItem = ({
  question,
  answer
}: {
  question: string;
  answer: string;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  return <div className="border-b border-gray-800 last:border-0" data-id="element-2231">
      <button onClick={() => setIsOpen(!isOpen)} className="w-full py-6 flex items-center justify-between text-left focus:outline-none group" data-id="element-2232">
        <span className="text-lg font-bold text-white group-hover:text-yellow-500 transition-colors" data-id="element-2233">
          {question}
        </span>
        <ChevronDownIcon className={`w-5 h-5 text-gray-400 transition-transform duration-300 flex-shrink-0 ml-4 ${isOpen ? 'rotate-180 text-yellow-500' : ''}`} data-id="element-2234" />
      </button>
      <AnimatePresence data-id="element-2235">
        {isOpen && <motion.div initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} transition={{
        duration: 0.3,
        ease: 'easeInOut'
      }} className="overflow-hidden" data-id="element-2236">
            <p className="pb-6 text-gray-400 leading-relaxed" data-id="element-2237">{answer}</p>
          </motion.div>}
      </AnimatePresence>
    </div>;
};
export function ReputationManagementPage() {
  return <div className="bg-viridia-black min-h-screen" data-id="element-2238">
      <SEOHead title="Reputation Management | Automate Reviews | Viridia Analytics" description="Turn happy customers into public proof. Manage reviews across platforms, automate review requests, and build trust for your local service business." canonical="/reputation-management" keywords="reputation management Phoenix, automated review requests, get more Google reviews, local business reputation, review management software" faq={faqs} data-id="element-2239" />

      {/* HERO SECTION */}
      <section className="relative pt-32 pb-20 lg:pt-48 lg:pb-32 overflow-hidden lg:min-h-[90vh] flex items-center" data-id="element-2240">
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden z-0 pointer-events-none" data-id="element-2241">
          <div className="absolute top-[-10%] left-[-10%] w-[50%] h-[50%] rounded-full bg-yellow-500/10 blur-[120px] animate-pulse-slow" data-id="element-2242"></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 w-full" data-id="element-2243">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-8 items-center" data-id="element-2244">
            {/* Hero Content */}
            <div className="max-w-2xl" data-id="element-2245">
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.5
            }} data-id="element-2246">
                <div className="inline-flex items-center px-3 py-1 rounded-full bg-yellow-500/10 border border-yellow-500/20 text-yellow-500 text-sm font-semibold tracking-wide mb-6" data-id="element-2247">
                  REPUTATION MANAGEMENT
                </div>
                <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-white leading-tight mb-6 tracking-tight" data-id="element-2248">
                  Turn Happy Customers Into{' '}
                  <span className="text-yellow-500" data-id="element-2249">Public Proof</span>
                </h1>
                <p className="text-lg text-gray-400 mb-8 leading-relaxed" data-id="element-2250">
                  Manage your reviews across major platforms, automate review
                  requests after completed jobs, and maintain a stronger online
                  reputation that supports more conversions.
                </p>
                <div className="flex flex-col sm:flex-row gap-4" data-id="element-2251">
                  <Link to="/contact" className="inline-flex justify-center items-center px-8 py-4 rounded-lg bg-viridia text-white font-semibold hover:bg-viridia-dark transition-colors duration-300 shadow-[0_0_20px_rgba(16,185,129,0.2)]" data-id="element-2252">
                    Book a Strategy Call
                    <ArrowRightIcon className="ml-2 w-5 h-5" data-id="element-2253" />
                  </Link>
                  <Link to="/pricing" className="inline-flex justify-center items-center px-8 py-4 rounded-lg bg-transparent border border-gray-600 text-white font-semibold hover:bg-white/5 transition-colors duration-300" data-id="element-2254">
                    View Packages
                  </Link>
                </div>
              </motion.div>
            </div>

            {/* Hero Visual - Reputation Control Center */}
            <motion.div initial={{
            opacity: 0,
            scale: 0.95
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 0.7,
            delay: 0.2
          }} className="relative hidden lg:block h-[500px]" data-id="element-2255">
              <div className="absolute inset-0 bg-gradient-to-br from-gray-900 to-viridia-surface rounded-2xl border border-gray-800 shadow-2xl overflow-hidden p-6 flex flex-col" data-id="element-2256">
                <div className="flex justify-between items-center mb-6 pb-4 border-b border-gray-800" data-id="element-2257">
                  <div className="text-sm font-bold text-white flex items-center" data-id="element-2258">
                    <StarIcon className="w-4 h-4 text-yellow-500 mr-2 fill-current" data-id="element-2259" />
                    Reputation Control Center
                  </div>
                  <div className="bg-green-500/10 text-green-400 text-xs px-2 py-1 rounded font-medium" data-id="element-2260">
                    System Active
                  </div>
                </div>

                <div className="flex-grow relative" data-id="element-2261">
                  {/* Floating Review Cards */}
                  <motion.div initial={{
                  y: 20,
                  opacity: 0
                }} animate={{
                  y: 0,
                  opacity: 1
                }} transition={{
                  delay: 0.5
                }} className="absolute top-4 left-4 right-12 bg-white rounded-lg p-4 shadow-lg z-20" data-id="element-2262">
                    <div className="flex justify-between items-start mb-2" data-id="element-2263">
                      <div className="flex text-yellow-400" data-id="element-2264">
                        {[...Array(5)].map((_, i) => <StarIcon key={i} className="w-4 h-4 fill-current" data-id="element-2265" />)}
                      </div>
                      <span className="text-xs text-gray-400" data-id="element-2266">
                        Google • 2m ago
                      </span>
                    </div>
                    <p className="text-sm text-gray-800 font-medium" data-id="element-2267">
                      "They showed up on time and fixed the AC in under an hour.
                      Highly recommend!"
                    </p>
                    <div className="mt-3 flex items-center text-xs text-viridia font-semibold" data-id="element-2268">
                      <CheckCircleIcon className="w-3 h-3 mr-1" data-id="element-2269" /> Auto-reply
                      sent
                    </div>
                  </motion.div>

                  <motion.div initial={{
                  y: 20,
                  opacity: 0
                }} animate={{
                  y: 0,
                  opacity: 1
                }} transition={{
                  delay: 0.8
                }} className="absolute top-32 left-12 right-4 bg-white rounded-lg p-4 shadow-lg z-10 opacity-90" data-id="element-2270">
                    <div className="flex justify-between items-start mb-2" data-id="element-2271">
                      <div className="flex text-yellow-400" data-id="element-2272">
                        {[...Array(5)].map((_, i) => <StarIcon key={i} className="w-4 h-4 fill-current" data-id="element-2273" />)}
                      </div>
                      <span className="text-xs text-gray-400" data-id="element-2274">
                        Review Platform • 1h ago
                      </span>
                    </div>
                    <p className="text-sm text-gray-800 font-medium" data-id="element-2275">
                      "Great communication from start to finish."
                    </p>
                  </motion.div>

                  {/* Automation Flow Graphic */}
                  <div className="absolute bottom-4 left-4 right-4 bg-viridia-black/80 backdrop-blur border border-gray-800 rounded-xl p-4" data-id="element-2276">
                    <div className="text-xs text-gray-400 mb-3 uppercase tracking-wider font-bold" data-id="element-2277">
                      Active Automation
                    </div>
                    <div className="flex items-center justify-between" data-id="element-2278">
                      <div className="text-center" data-id="element-2279">
                        <div className="w-8 h-8 rounded-full bg-blue-500/20 flex items-center justify-center mx-auto mb-1" data-id="element-2280">
                          <CheckCircleIcon className="w-4 h-4 text-blue-400" data-id="element-2281" />
                        </div>
                        <span className="text-[10px] text-gray-400" data-id="element-2282">
                          Job Done
                        </span>
                      </div>
                      <div className="flex-1 h-px bg-gray-700 mx-2 relative" data-id="element-2283">
                        <motion.div className="absolute top-1/2 left-0 w-2 h-2 bg-viridia rounded-full -translate-y-1/2" animate={{
                        left: ['0%', '100%']
                      }} transition={{
                        duration: 2,
                        repeat: Infinity,
                        ease: 'linear'
                      }} data-id="element-2284" />
                      </div>
                      <div className="text-center" data-id="element-2285">
                        <div className="w-8 h-8 rounded-full bg-viridia/20 flex items-center justify-center mx-auto mb-1" data-id="element-2286">
                          <MessageCircleIcon className="w-4 h-4 text-viridia" data-id="element-2287" />
                        </div>
                        <span className="text-[10px] text-gray-400" data-id="element-2288">
                          SMS Sent
                        </span>
                      </div>
                      <div className="flex-1 h-px bg-gray-700 mx-2" data-id="element-2289"></div>
                      <div className="text-center" data-id="element-2290">
                        <div className="w-8 h-8 rounded-full bg-yellow-500/20 flex items-center justify-center mx-auto mb-1" data-id="element-2291">
                          <StarIcon className="w-4 h-4 text-yellow-500 fill-current" data-id="element-2292" />
                        </div>
                        <span className="text-[10px] text-gray-400" data-id="element-2293">
                          Review
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* WHY IT MATTERS */}
      <section className="py-24 bg-viridia-black border-t border-gray-800" data-id="element-2294">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2295">
          <ScrollReveal data-id="element-2296">
            <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-2297">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-6" data-id="element-2298">
                Reviews Are Your Best Salespeople
              </h2>
              <p className="text-xl text-gray-400" data-id="element-2299">
                In local services, trust is everything. A strong reputation
                doesn't just get you more clicks - it allows you to charge
                premium prices.
              </p>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* FAQ SECTION */}
      <section className="py-24 bg-viridia-surface border-t border-gray-800" data-id="element-2300">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center mb-16" data-id="element-2301">
          <ScrollReveal data-id="element-2302">
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-6" data-id="element-2303">
              Frequently Asked Questions
            </h2>
            <p className="text-xl text-gray-400" data-id="element-2304">
              Everything you need to know about our reputation management
              system.
            </p>
          </ScrollReveal>
        </div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2305">
          <ScrollReveal delay={0.2} data-id="element-2306">
            <div className="bg-viridia-black border border-gray-800 rounded-2xl p-6 md:p-8" data-id="element-2307">
              {faqs.map((faq, index) => <FAQItem key={index} question={faq.question} answer={faq.answer} data-id="element-2308" />)}
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* CTA SECTION */}
      <section className="py-24 bg-gradient-to-br from-viridia-dark to-viridia relative overflow-hidden" data-id="element-2309">
        <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-10 mix-blend-overlay" data-id="element-2310"></div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center" data-id="element-2311">
          <ScrollReveal data-id="element-2312">
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-6" data-id="element-2313">
              Ready to Build a Better Reputation?
            </h2>
            <p className="text-xl text-white/90 mb-10 max-w-2xl mx-auto" data-id="element-2314">
              Strengthen trust, improve consistency, and build a system for
              turning happy customers into reviews that win more business.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4" data-id="element-2315">
              <Link to="/contact" className="inline-flex justify-center items-center px-8 py-4 rounded-lg bg-white text-viridia-dark font-bold text-lg hover:bg-gray-100 transition-colors duration-300 shadow-xl hover:shadow-2xl transform hover:-translate-y-1" data-id="element-2316">
                Book a Strategy Call
                <ArrowRightIcon className="ml-2 w-5 h-5" data-id="element-2317" />
              </Link>
              <Link to="/pricing" className="inline-flex justify-center items-center px-8 py-4 rounded-lg bg-transparent border-2 border-white text-white font-bold text-lg hover:bg-white/10 transition-colors duration-300" data-id="element-2318">
                View Packages
              </Link>
            </div>
          </ScrollReveal>
        </div>
      </section>
    </div>;
}